import { useOwnerAuth } from "@/components/owner/OwnerAuthProvider";
import { db } from "@/lib/firebase";
import {
  buildBusinessPublicCardLink,
  deactivateApprovedBusiness,
  deleteApprovedBusiness,
  deleteDraftBusiness,
  persistOwnerOnboardingStep,
  readOwnerListingSubscriptionSummary,
  reactivateApprovedBusiness,
  withdrawPendingBusiness,
  type OwnerListingSubscriptionSummary,
} from "@/lib/onboardingBusiness";
import {
  normalizeOwnerOnboardingStatus,
  resolveOwnerDashboardLifecycle,
  resolveOwnerOnboardingResumePath,
  selectOwnerLifecycleBusiness,
} from "@/lib/onboardingResume";
import {
  clearOwnerCorrectionSession,
  startOwnerCorrectionSession,
} from "@/lib/ownerCorrectionSession";
import { googleSignOut } from "@/lib/authGoogle";
import * as Clipboard from "expo-clipboard";
import * as FileSystem from "expo-file-system/legacy";
import * as MediaLibrary from "expo-media-library";
import {
  isValidWhatsAppCatalogUrl,
  isValidWhatsAppChatNumber,
  normalizeWhatsAppCtaType,
} from "@/lib/whatsapp";
import { router } from "expo-router";
import {
  collection,
  doc,
  limit,
  onSnapshot,
  query,
  where,
} from "firebase/firestore";
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  ActivityIndicator,
  Alert,
  Modal,
  Platform,
  Pressable,
  ScrollView,
  Share,
  Text,
  useWindowDimensions,
  View,
} from "react-native";
import QRCode from "react-native-qrcode-svg";
import { SafeAreaView } from "react-native-safe-area-context";
import { captureRef } from "react-native-view-shot";

type BusinessStatus =
  | "draft"
  | "pending"
  | "approved"
  | "rejected"
  | "changes_requested";

type OwnerDoc = {
  uid?: string;
  userId?: string;
  name?: string;
  displayName?: string;
  fullName?: string;
  email?: string;
  phone?: string;
  phoneNumber?: string;
  onboardingStep?: unknown;
  createdAt?: unknown;
};

type BizDoc = {
  name?: string;
  slug?: string;
  logoUrl?: string;

  accountNumber?: string;
  accountNumberCreatedAt?: unknown;

  category?: string;
  categoryName?: string;
  subCategory?: string[];
  subcategoryName?: string;

  phone?: string;
  website?: string;
  email?: string;

  facebook?: string;
  instagram?: string;
  twitter?: string;
  tiktok?: string;
  youtube?: string;
  whatsappCtaType?: "chat" | "catalog";
  whatsappChatNumber?: string;
  whatsappCatalogUrl?: string;

  items?: unknown[];

  status?: BusinessStatus;
  isPublished?: boolean;
  isDeleted?: boolean;
  isDeactivated?: boolean;

  subscriptionType?: string;
  subscriptionPlan?: string;
  subscriptionStatus?: string;
  subscriptionStartedAt?: unknown;
  subscriptionExpiresAt?: unknown;
  expiresAt?: unknown;
  validUntil?: unknown;
  endsAt?: unknown;
  pioneerSlotId?: string | null;

  ownerId?: string;

  onboardingStep?: string;
  onboardingCompleted?: boolean;

  submittedAt?: unknown;
  approvedAt?: unknown;
  rejectedAt?: unknown;
  changesRequestedAt?: unknown;

  rejectedReason?: string | null;
  changesRequestMessage?: string | null;

  createdAt?: unknown;
  updatedAt?: unknown;
  deactivatedAt?: unknown;
};

type BizRow = { id: string } & BizDoc;

type QrSvgRef = {
  toDataURL?: (
    callback: (data: string) => void,
    options?: { width?: number; height?: number }
  ) => void;
};

type WebShareNavigator = Navigator & {
  canShare?: (data: {
    files?: File[];
    title?: string;
    text?: string;
  }) => boolean;
  share?: (data: {
    files?: File[];
    title?: string;
    text?: string;
  }) => Promise<void>;
};

type QrBusinessData = {
  businessId: string;
  businessName: string;
  cardLink: string;
  fileName: string;
};

type QrPreviewAction = "download" | "share" | "copy";

type WebQrDownloadOptions = {
  preferMobileShare?: boolean;
  allowShareFallback?: boolean;
};

const QR_CARD_WIDTH = 1080;
const QR_CARD_HEIGHT = 1350;
const QR_CARD_QR_SIZE = 760;
const QR_CARD_QR_TOP = 170;
const QR_CARD_LABEL_TOP = 1000;
const QR_CARD_LABEL_MAX_WIDTH = 920;
const QR_CARD_RENDER_SCALE = 2 / 3;

const QR_RENDER_CARD_WIDTH = QR_CARD_WIDTH * QR_CARD_RENDER_SCALE;
const QR_RENDER_CARD_HEIGHT = QR_CARD_HEIGHT * QR_CARD_RENDER_SCALE;
const QR_RENDER_QR_SIZE = QR_CARD_QR_SIZE * QR_CARD_RENDER_SCALE;
const QR_RENDER_QR_TOP = QR_CARD_QR_TOP * QR_CARD_RENDER_SCALE;
const QR_RENDER_LABEL_TOP = QR_CARD_LABEL_TOP * QR_CARD_RENDER_SCALE;
const QR_RENDER_LABEL_MAX_WIDTH =
  QR_CARD_LABEL_MAX_WIDTH * QR_CARD_RENDER_SCALE;

function toMillis(value: unknown): number {
  if (!value) return 0;

  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }

  if (value instanceof Date) {
    return value.getTime();
  }

  if (
    typeof value === "object" &&
    "toMillis" in value &&
    typeof (value as { toMillis?: unknown }).toMillis === "function"
  ) {
    return (value as { toMillis: () => number }).toMillis();
  }

  if (
    typeof value === "object" &&
    "toDate" in value &&
    typeof (value as { toDate?: unknown }).toDate === "function"
  ) {
    return (value as { toDate: () => Date }).toDate().getTime();
  }

  return 0;
}

function addOneYear(value: unknown): Date | null {
  const ms = toMillis(value);

  if (!ms) return null;

  const date = new Date(ms);
  date.setFullYear(date.getFullYear() + 1);

  return date;
}

function formatDate(value: unknown): string {
  const ms = toMillis(value);

  if (!ms) return "Not set";

  try {
    return new Date(ms).toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return "Not set";
  }
}

function normalizeStatus(value: unknown): BusinessStatus | null {
  return normalizeOwnerOnboardingStatus(value);
}

function titleCase(value: string) {
  const text = value.trim();

  if (!text) return "Not set";

  return text.charAt(0).toUpperCase() + text.slice(1);
}

function getStatusLabel(business: BizRow) {
  const status = normalizeStatus(business.status);

  if (business.isDeactivated === true) return "Deactivated";
  if (status === "changes_requested") return "Changes requested";

  return status ? titleCase(status) : "Unknown status";
}

function shouldShowAccountNumber(status: BusinessStatus | null) {
  return status === "draft" || status === "pending" || status === "approved";
}

function getAccountNumberLabel(business: BizRow) {
  const accountNumber = business.accountNumber?.trim();

  return accountNumber || "Not set";
}

function getStatusColors(business: BizRow) {
  const status = normalizeStatus(business.status);

  if (business.isDeactivated === true) {
    return {
      bg: "#F3F4F6",
      border: "#D1D5DB",
      text: "#374151",
    };
  }

  switch (status) {
    case "approved":
      return {
        bg: "#DCFCE7",
        border: "#86EFAC",
        text: "#166534",
      };

    case "pending":
      return {
        bg: "#FEF3C7",
        border: "#FCD34D",
        text: "#92400E",
      };

    case "changes_requested":
      return {
        bg: "#DBEAFE",
        border: "#93C5FD",
        text: "#1E40AF",
      };

    case "rejected":
      return {
        bg: "#FEE2E2",
        border: "#FCA5A5",
        text: "#991B1B",
      };

    case "draft":
    default:
      return {
        bg: "#F3F4F6",
        border: "#E5E7EB",
        text: "#374151",
      };
  }
}

function getPrimaryActionLabel(status: BusinessStatus | null) {
  switch (status) {
    case "changes_requested":
      return "Start Corrections";

    case "rejected":
      return "Start Corrections";

    case "draft":
    default:
      return "Continue Onboarding";
  }
}

function isPioneerBusiness(
  business: BizRow,
  subscription: OwnerListingSubscriptionSummary | null
) {
  const type = `${business.subscriptionType || ""} ${
    business.subscriptionPlan || ""
  } ${subscription?.planName || ""}`.toLowerCase();

  return (
    !!business.pioneerSlotId ||
    subscription?.source === "pioneer" ||
    type.includes("pioneer")
  );
}

function getSubscriptionPlanLabel(
  business: BizRow,
  subscription: OwnerListingSubscriptionSummary | null
) {
  return (
    business.subscriptionType?.trim() ||
    business.subscriptionPlan?.trim() ||
    subscription?.planName ||
    (business.pioneerSlotId ? "Pioneer" : "Not set")
  );
}

function getOwnerSubscriptionStatusDisplay(value: string) {
  const status = value.trim().toLowerCase();

  if (status === "used") return "Active";
  if (status === "active") return "Active";
  if (status === "available") return "Available";
  if (status === "expired") return "Expired";
  if (status === "inactive") return "Inactive";
  if (status === "none") return "Not set";

  return titleCase(status);
}

function getSubscriptionStatusLabel(
  business: BizRow,
  subscription: OwnerListingSubscriptionSummary | null,
  loadingSubscription: boolean
) {
  if (business.subscriptionStatus?.trim()) {
    return getOwnerSubscriptionStatusDisplay(business.subscriptionStatus);
  }

  if (loadingSubscription) {
    return "Loading...";
  }

  if (subscription?.status) {
    return getOwnerSubscriptionStatusDisplay(subscription.status);
  }

  if (subscription?.active) {
    return "Active";
  }

  return "Not set";
}

function getExpiryValue(
  business: BizRow,
  subscription: OwnerListingSubscriptionSummary | null
) {
  const explicitExpiry =
    business.subscriptionExpiresAt ||
    business.endsAt ||
    business.expiresAt ||
    business.validUntil ||
    subscription?.endsAt ||
    subscription?.expiresAt ||
    subscription?.validUntil ||
    null;

  if (explicitExpiry) {
    return explicitExpiry;
  }

  if (isPioneerBusiness(business, subscription)) {
    return (
      addOneYear(business.subscriptionStartedAt) ||
      addOneYear(business.createdAt) ||
      addOneYear(subscription?.startedAt) ||
      addOneYear(subscription?.createdAt)
    );
  }

  return null;
}

function isLiveApprovedBusiness(business: BizRow) {
  return (
    normalizeStatus(business.status) === "approved" &&
    business.isPublished === true &&
    business.isDeleted !== true &&
    business.isDeactivated !== true
  );
}

function hasAnySocialOrWebsite(business: BizRow) {
  return !!(
    business.website ||
    business.email ||
    business.facebook ||
    business.instagram ||
    business.twitter ||
    business.tiktok ||
    business.youtube
  );
}

function buildAttentionItems(business: BizRow): string[] {
  const items: string[] = [];
  const whatsappCtaType = normalizeWhatsAppCtaType(business.whatsappCtaType);

  if (!business.phone?.trim()) {
    items.push("Add a business phone number.");
  }

  if (
    whatsappCtaType === "chat" &&
    !isValidWhatsAppChatNumber(business.whatsappChatNumber)
  ) {
    items.push("Add a valid WhatsApp chat number.");
  }

  if (
    whatsappCtaType === "catalog" &&
    !isValidWhatsAppCatalogUrl(business.whatsappCatalogUrl)
  ) {
    items.push("Add a valid WhatsApp catalog link or switch to chat.");
  }

  if (!hasAnySocialOrWebsite(business)) {
    items.push("Add a website, email, or social media link.");
  }

  if (!Array.isArray(business.items) || business.items.length < 3) {
    items.push("Add at least 3 products or services.");
  }

  return items;
}

function makeQrHelperText(businessName?: string) {
  const name = String(businessName || "").trim();

  return `Soko Grid: ${name || "Business"}`;
}

function getQrCaptionFontSize(label: string) {
  const length = String(label || "").length;

  if (length <= 28) return 56;
  if (length <= 40) return 50;
  if (length <= 55) return 44;
  if (length <= 70) return 38;

  return 34;
}

function safeFileName(value: string) {
  const clean = value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

  return clean || "soko-grid-card";
}

function getQrBusinessData(business: BizRow): QrBusinessData | null {
  const slug = business.slug?.trim();

  if (!slug) return null;

  const businessName = business.name?.trim() || slug;

  return {
    businessId: business.id,
    businessName,
    cardLink: buildBusinessPublicCardLink(slug),
    fileName: `${safeFileName(businessName)}-qr-code.png`,
  };
}

function showNoPublicQrLinkAlert() {
  Alert.alert(
    "No public link yet",
    "This business does not have a public slug yet."
  );
}

function wait(ms: number) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function scaleQrCardValue(value: number) {
  return value * QR_CARD_RENDER_SCALE;
}

function isMobileWebBrowser() {
  if (typeof navigator === "undefined") return false;

  return /Android|iPhone|iPad|iPod|Mobile/i.test(navigator.userAgent || "");
}

function shouldOpenMobileQrPreview() {
  return Platform.OS !== "web" || isMobileWebBrowser();
}

function canUseBrowserDownload() {
  return (
    typeof document !== "undefined" &&
    typeof URL !== "undefined" &&
    typeof HTMLAnchorElement !== "undefined" &&
    "download" in HTMLAnchorElement.prototype
  );
}

function getQrPngDataUrl(svgRef: QrSvgRef | null): Promise<string> {
  return new Promise((resolve, reject) => {
    if (!svgRef?.toDataURL) {
      reject(new Error("QR image is not ready yet."));
      return;
    }

    let settled = false;
    const timeout = setTimeout(() => {
      if (settled) return;
      settled = true;
      reject(new Error("QR image generation timed out."));
    }, 5000);

    try {
      svgRef.toDataURL(
        (data) => {
          if (settled) return;
          settled = true;
          clearTimeout(timeout);

          const cleanData = String(data || "").trim();

          if (!cleanData) {
            reject(new Error("QR image generation returned an empty image."));
            return;
          }

          resolve(
            cleanData.startsWith("data:image/")
              ? cleanData
              : `data:image/png;base64,${cleanData}`
          );
        },
        { width: QR_CARD_QR_SIZE, height: QR_CARD_QR_SIZE }
      );
    } catch (error) {
      if (settled) return;
      settled = true;
      clearTimeout(timeout);
      reject(error);
    }
  });
}

function loadImageFromDataUrl(dataUrl: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    if (typeof Image === "undefined") {
      reject(new Error("This browser cannot render the QR image."));
      return;
    }

    const image = new Image();

    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error("Could not render the QR image."));
    image.src = dataUrl;
  });
}

function fitCanvasText(
  context: CanvasRenderingContext2D,
  text: string,
  maxWidth: number
) {
  if (context.measureText(text).width <= maxWidth) return text;

  let trimmed = text.trim();

  while (trimmed.length > 0) {
    const candidate = `${trimmed}...`;

    if (context.measureText(candidate).width <= maxWidth) {
      return candidate;
    }

    trimmed = trimmed.slice(0, -1).trimEnd();
  }

  return "...";
}

function getCanvasTextLines(
  context: CanvasRenderingContext2D,
  text: string,
  maxWidth: number,
  maxLines: number
) {
  const words = text.split(/\s+/g).filter(Boolean);
  const lines: string[] = [];
  let currentLine = "";

  words.forEach((word) => {
    const candidate = currentLine ? `${currentLine} ${word}` : word;

    if (context.measureText(candidate).width <= maxWidth) {
      currentLine = candidate;
      return;
    }

    if (currentLine) {
      lines.push(currentLine);
      currentLine = word;
    } else {
      lines.push(fitCanvasText(context, word, maxWidth));
      currentLine = "";
    }
  });

  if (currentLine) {
    lines.push(currentLine);
  }

  if (lines.length <= maxLines) {
    return lines;
  }

  const visibleLines = lines.slice(0, maxLines);
  visibleLines[maxLines - 1] = fitCanvasText(
    context,
    lines.slice(maxLines - 1).join(" "),
    maxWidth
  );

  return visibleLines;
}

async function createBrandedQrCardDataUrl(qrDataUrl: string, label: string) {
  if (typeof document === "undefined") {
    throw new Error("This browser cannot create a QR card image.");
  }

  const canvas = document.createElement("canvas");
  canvas.width = QR_CARD_WIDTH;
  canvas.height = QR_CARD_HEIGHT;

  const context = canvas.getContext("2d");

  if (!context) {
    throw new Error("This browser cannot draw the QR card image.");
  }

  const qrImage = await loadImageFromDataUrl(qrDataUrl);
  const qrLeft = (QR_CARD_WIDTH - QR_CARD_QR_SIZE) / 2;
  const fontSize = getQrCaptionFontSize(label);
  const lineHeight = fontSize + 12;

  context.fillStyle = "#FFFFFF";
  context.fillRect(0, 0, QR_CARD_WIDTH, QR_CARD_HEIGHT);

  context.imageSmoothingEnabled = false;
  context.drawImage(
    qrImage,
    qrLeft,
    QR_CARD_QR_TOP,
    QR_CARD_QR_SIZE,
    QR_CARD_QR_SIZE
  );

  context.imageSmoothingEnabled = true;
  context.fillStyle = "#111827";
  context.font = `800 ${fontSize}px Arial, sans-serif`;
  context.textAlign = "center";
  context.textBaseline = "top";

  const lines = getCanvasTextLines(
    context,
    label,
    QR_CARD_LABEL_MAX_WIDTH,
    2
  );

  lines.forEach((line, index) => {
    context.fillText(
      line,
      QR_CARD_WIDTH / 2,
      QR_CARD_LABEL_TOP + index * lineHeight
    );
  });

  return canvas.toDataURL("image/png");
}

async function dataUrlToBlob(dataUrl: string) {
  if (typeof fetch !== "function") {
    throw new Error("This browser cannot prepare the QR image for download.");
  }

  const response = await fetch(dataUrl);
  return response.blob();
}

async function shareQrFileIfAvailable(blob: Blob, fileName: string) {
  if (typeof navigator === "undefined" || typeof File === "undefined") {
    return false;
  }

  const webNavigator = navigator as WebShareNavigator;

  if (
    typeof webNavigator.share !== "function" ||
    typeof webNavigator.canShare !== "function"
  ) {
    return false;
  }

  const file = new File([blob], fileName, { type: "image/png" });
  const shareData = {
    files: [file],
    title: "Soko Grid QR code",
    text: "Save or share this QR code to open the public business card.",
  };

  if (!webNavigator.canShare(shareData)) {
    return false;
  }

  try {
    await webNavigator.share(shareData);
    return true;
  } catch (error: any) {
    return error?.name === "AbortError";
  }
}

function downloadBlobInBrowser(blob: Blob, fileName: string) {
  if (!canUseBrowserDownload()) return false;

  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");

  anchor.href = url;
  anchor.download = fileName;
  anchor.rel = "noopener";
  anchor.style.display = "none";

  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();

  setTimeout(() => URL.revokeObjectURL(url), 1000);

  return true;
}

function openDataUrlInNewTab(dataUrl: string) {
  if (typeof window === "undefined") return false;

  const opened = window.open(dataUrl, "_blank", "noopener,noreferrer");

  if (opened) {
    opened.opener = null;
    return true;
  }

  if (typeof document === "undefined") return false;

  const anchor = document.createElement("a");
  anchor.href = dataUrl;
  anchor.target = "_blank";
  anchor.rel = "noopener noreferrer";
  anchor.click();

  return true;
}

function showDashboardMessage(title: string, message: string) {
  if (
    Platform.OS === "web" &&
    typeof window !== "undefined" &&
    typeof window.alert === "function"
  ) {
    window.alert(`${title}\n\n${message}`);
    return;
  }

  Alert.alert(title, message);
}

export default function OwnerDashboard() {
  const qrCaptureRef = useRef<View | null>(null);
  const qrSvgRef = useRef<QrSvgRef | null>(null);
  const { width: viewportWidth } = useWindowDimensions();

  const { authInitialized, user, ownerReady, ownerError } = useOwnerAuth();
  const [owner, setOwner] = useState<OwnerDoc | null>(null);
  const [businesses, setBusinesses] = useState<BizRow[]>([]);
  const [subscription, setSubscription] =
    useState<OwnerListingSubscriptionSummary | null>(null);

  const [loadingBusinesses, setLoadingBusinesses] = useState(true);
  const [loadingSubscription, setLoadingSubscription] = useState(false);
  const [busyBusinessId, setBusyBusinessId] = useState<string | null>(null);
  const [signingOut, setSigningOut] = useState(false);

  const [qrRenderData, setQrRenderData] = useState<{
    businessName: string;
    cardLink: string;
  } | null>(null);
  const [qrPreviewData, setQrPreviewData] = useState<QrBusinessData | null>(
    null
  );
  const [qrPreviewAction, setQrPreviewAction] =
    useState<QrPreviewAction | null>(null);

  const ownerId = user?.uid ?? null;
  const qrCaption = qrRenderData
    ? makeQrHelperText(qrRenderData.businessName)
    : "";
  const qrCaptionFontSize = getQrCaptionFontSize(qrCaption);
  const qrPreviewCaption = qrPreviewData
    ? makeQrHelperText(qrPreviewData.businessName)
    : "";
  const qrPreviewSize = Math.min(260, Math.max(190, viewportWidth - 96));
  const qrPreviewBusy = qrPreviewAction !== null;

  useEffect(() => {
    if (authInitialized && !user) {
      router.replace("/(owner)/onboarding/signin" as any);
    }
  }, [authInitialized, user]);

  useEffect(() => {
    if (!ownerId) return;
    void clearOwnerCorrectionSession(ownerId);
  }, [ownerId]);

  useEffect(() => {
    if (!ownerId) {
      setOwner(null);
      return;
    }

    const ownerRef = doc(db, "owners", ownerId);

    const unsub = onSnapshot(
      ownerRef,
      (snap) => {
        setOwner((snap.data() as OwnerDoc) || null);
      },
      () => {
        console.warn("Owner dashboard profile listener failed.");
        setOwner(null);
      }
    );

    return () => unsub();
  }, [ownerId]);

  useEffect(() => {
    let cancelled = false;

    async function loadSubscription() {
      if (!ownerId) {
        setSubscription(null);
        return;
      }

      setLoadingSubscription(true);

      try {
        const summary = await readOwnerListingSubscriptionSummary(ownerId);

        if (!cancelled) {
          setSubscription(summary);
        }
      } catch {
        console.warn("Owner dashboard subscription load failed.");

        if (!cancelled) {
          setSubscription(null);
        }
      } finally {
        if (!cancelled) {
          setLoadingSubscription(false);
        }
      }
    }

    void loadSubscription();

    return () => {
      cancelled = true;
    };
  }, [ownerId]);

  useEffect(() => {
    if (!ownerId) {
      setBusinesses([]);
      setLoadingBusinesses(false);
      return;
    }

    setLoadingBusinesses(true);

    const q = query(
      collection(db, "businesses"),
      where("ownerId", "==", ownerId),
      limit(50)
    );

    const unsub = onSnapshot(
      q,
      (snap) => {
        const rows = snap.docs
          .map((docSnap) => ({
            id: docSnap.id,
            ...(docSnap.data() as BizDoc),
          }))
          .filter((business) => business.isDeleted !== true)
          .sort((a, b) => {
            const aMs = toMillis(a.updatedAt) || toMillis(a.createdAt);
            const bMs = toMillis(b.updatedAt) || toMillis(b.createdAt);

            return bMs - aMs;
          });

        setBusinesses(rows);
        setLoadingBusinesses(false);
      },
      (error) => {
        console.warn("Owner dashboard business listener failed.");
        setBusinesses([]);
        setLoadingBusinesses(false);

        Alert.alert(
          "Could not load businesses",
          error?.message ||
            "We could not load your business profiles. Please try again."
        );
      }
    );

    return () => unsub();
  }, [ownerId]);

  const loading = !authInitialized || (!!user && !ownerReady) || loadingBusinesses;

  const displayName = useMemo(() => {
    return (
      owner?.displayName ||
      owner?.fullName ||
      owner?.name ||
      user?.displayName ||
      user?.email ||
      user?.phoneNumber ||
      "Owner"
    );
  }, [owner, user]);

  const authoritativeBusiness = useMemo(
    () => selectOwnerLifecycleBusiness(businesses),
    [businesses]
  );

  const draftOrEditableBusiness = useMemo(() => {
    return normalizeStatus(authoritativeBusiness?.status) === "draft"
      ? authoritativeBusiness
      : null;
  }, [authoritativeBusiness]);

  const hasExistingBusiness = businesses.length > 0;

  const goDirectory = () => {
    router.push("/directory" as any);
  };

  const goCreateOrContinue = () => {
    if (draftOrEditableBusiness?.id) {
      const status = normalizeStatus(draftOrEditableBusiness.status);
      const pathname =
        status === "draft"
          ? resolveOwnerOnboardingResumePath({
              status,
              onboardingStep: draftOrEditableBusiness.onboardingStep,
              ownerOnboardingStep: owner?.onboardingStep,
            })
          : "/(owner)/onboarding/basics";

      router.push({
        pathname,
        params: { businessId: draftOrEditableBusiness.id },
      } as any);

      return;
    }

    if (hasExistingBusiness) {
      Alert.alert(
        "Business already exists",
        "You already have a business profile connected to this account. Please manage it from your dashboard."
      );
      return;
    }

    router.push("/(owner)/start" as any);
  };

  const goEdit = async (
    business: BizRow,
    mode: "onboarding" | "approved"
  ) => {
    const status = normalizeStatus(business.status);

    if (
      mode === "onboarding" &&
      (status === "rejected" || status === "changes_requested")
    ) {
      if (!ownerId) return;

      try {
        await clearOwnerCorrectionSession(ownerId);
        await startOwnerCorrectionSession(ownerId, business.id);
      } catch {
        Alert.alert(
          "Could not start corrections",
          "Please try again from your dashboard."
        );
        return;
      }
    }

    const pathname =
      mode === "approved"
        ? "/(owner)/onboarding/basics"
        : status === "draft"
        ? resolveOwnerOnboardingResumePath({
            status,
            onboardingStep: business.onboardingStep,
            ownerOnboardingStep: owner?.onboardingStep,
          })
        : "/(owner)/onboarding/basics";

    router.push({
      pathname,
      params: {
        businessId: business.id,
        mode: mode === "approved" ? "edit-approved" : "onboarding",
      },
    } as any);
  };

  const goManageItems = (
    businessId: string,
    mode: "onboarding" | "approved"
  ) => {
    router.push({
      pathname: "/(owner)/onboarding/items",
      params: {
        businessId,
        mode: mode === "approved" ? "edit-approved" : "onboarding",
      },
    } as any);
  };

  const goView = (slug?: string) => {
    if (!slug) {
      Alert.alert(
        "No public link yet",
        "This business does not have a public slug yet."
      );
      return;
    }

    router.push({
      pathname: "/p/[slug]",
      params: { slug },
    } as any);
  };

  const copyCardLink = async (business: BizRow) => {
    const slug = business.slug?.trim();

    if (!slug) {
      Alert.alert(
        "No public link yet",
        "This business does not have a public slug yet."
      );
      return;
    }

    const link = buildBusinessPublicCardLink(slug);

    try {
      await Clipboard.setStringAsync(link);
      Alert.alert("Copied", "The public card link has been copied.");
    } catch (error: any) {
      Alert.alert("Copy failed", error?.message || "Could not copy the link.");
    }
  };

  const shareCardLink = async (business: BizRow) => {
    const slug = business.slug?.trim();

    if (!slug) {
      Alert.alert(
        "No public link yet",
        "This business does not have a public slug yet."
      );
      return;
    }

    const link = buildBusinessPublicCardLink(slug);

    try {
      await Share.share({
        title: business.name || "Soko Grid business card",
        message: `View ${
          business.name || "this business"
        } on Soko Grid:\n${link}`,
      });
    } catch {
      // User may cancel the native share sheet.
    }
  };

  const prepareQrRender = async (qrData: QrBusinessData) => {
    qrSvgRef.current = null;

    setQrRenderData({
      businessName: qrData.businessName,
      cardLink: qrData.cardLink,
    });

    await wait(300);
  };

  const createWebBrandedQrCard = async (qrData: QrBusinessData) => {
    await prepareQrRender(qrData);

    const qrDataUrl = await getQrPngDataUrl(qrSvgRef.current);
    const cardDataUrl = await createBrandedQrCardDataUrl(
      qrDataUrl,
      makeQrHelperText(qrData.businessName)
    );
    const blob = await dataUrlToBlob(cardDataUrl);

    return { blob, cardDataUrl };
  };

  const downloadQrCodeOnWeb = async (
    qrData: QrBusinessData,
    options: WebQrDownloadOptions = {}
  ) => {
    const { blob, cardDataUrl } = await createWebBrandedQrCard(qrData);
    const allowShareFallback = options.allowShareFallback !== false;

    if (
      options.preferMobileShare &&
      isMobileWebBrowser() &&
      (await shareQrFileIfAvailable(blob, qrData.fileName))
    ) {
      return;
    }

    if (downloadBlobInBrowser(blob, qrData.fileName)) {
      Alert.alert(
        "Download started",
        "The QR code PNG is being downloaded by your browser."
      );
      return;
    }

    if (
      allowShareFallback &&
      (await shareQrFileIfAvailable(blob, qrData.fileName))
    ) {
      return;
    }

    if (openDataUrlInNewTab(cardDataUrl)) {
      Alert.alert(
        "QR code opened",
        "Use your browser's save or share option to keep the QR image."
      );
      return;
    }

    throw new Error("This browser could not download or open the QR image.");
  };

  const captureQrCodeToCacheFile = async (qrData: QrBusinessData) => {
    await prepareQrRender(qrData);

    if (!qrCaptureRef.current) {
      throw new Error("QR image is not ready yet.");
    }

    const capturedUri = await captureRef(qrCaptureRef.current, {
      format: "png",
      quality: 1,
      result: "tmpfile",
      width: QR_CARD_WIDTH,
      height: QR_CARD_HEIGHT,
    });

    const folder = FileSystem.cacheDirectory || FileSystem.documentDirectory;

    if (!folder) {
      throw new Error("Could not access device storage.");
    }

    const finalUri = `${folder}${qrData.fileName}`;

    await FileSystem.copyAsync({
      from: capturedUri,
      to: finalUri,
    });

    return finalUri;
  };

  const saveQrCodeToDevice = async (qrData: QrBusinessData) => {
    const permission = await MediaLibrary.requestPermissionsAsync();

    if (!permission.granted) {
      Alert.alert(
        "Permission needed",
        "Allow photo library access so Soko Grid can save the QR code to your device."
      );
      return;
    }

    const finalUri = await captureQrCodeToCacheFile(qrData);

    await MediaLibrary.saveToLibraryAsync(finalUri);

    Alert.alert("Saved", "The QR code has been saved to your device.");
  };

  const shareQrCodeOnWeb = async (qrData: QrBusinessData) => {
    const { blob, cardDataUrl } = await createWebBrandedQrCard(qrData);

    if (await shareQrFileIfAvailable(blob, qrData.fileName)) {
      return;
    }

    if (openDataUrlInNewTab(cardDataUrl)) {
      Alert.alert(
        "QR code opened",
        "This browser cannot share QR images directly. Use the opened branded QR image to share or save."
      );
      return;
    }

    throw new Error("This browser could not share or open the QR image.");
  };

  const shareQrCodeFromDevice = async (qrData: QrBusinessData) => {
    if (Platform.OS === "android") {
      throw new Error(
        "Sharing QR images is not available on Android yet. Use Download QR Code to save the branded image."
      );
    }

    const finalUri = await captureQrCodeToCacheFile(qrData);

    await Share.share({
      title: "Soko Grid QR code",
      message: `Scan this QR code to open ${qrData.businessName} on Soko Grid.`,
      url: finalUri,
    });
  };

  const downloadQrCode = async (business: BizRow) => {
    const qrData = getQrBusinessData(business);

    if (!qrData) {
      showNoPublicQrLinkAlert();
      return;
    }

    try {
      setBusyBusinessId(business.id);

      if (Platform.OS === "web") {
        await downloadQrCodeOnWeb(qrData, {
          preferMobileShare: true,
          allowShareFallback: true,
        });
        return;
      }

      await saveQrCodeToDevice(qrData);
    } catch (error: any) {
      Alert.alert(
        "QR download failed",
        error?.message || "Could not download the QR code."
      );
    } finally {
      setQrRenderData(null);
      setBusyBusinessId(null);
    }
  };

  const openQrPreview = (business: BizRow) => {
    const qrData = getQrBusinessData(business);

    if (!qrData) {
      showNoPublicQrLinkAlert();
      return;
    }

    qrSvgRef.current = null;
    setQrPreviewData(qrData);
    setQrRenderData({
      businessName: qrData.businessName,
      cardLink: qrData.cardLink,
    });
  };

  const handleQrButtonPress = (business: BizRow) => {
    if (shouldOpenMobileQrPreview()) {
      openQrPreview(business);
      return;
    }

    void downloadQrCode(business);
  };

  const closeQrPreview = () => {
    if (qrPreviewBusy) return;

    qrSvgRef.current = null;
    setQrPreviewData(null);
    setQrRenderData(null);
  };

  const downloadQrCodeFromPreview = async () => {
    if (!qrPreviewData) return;

    try {
      setQrPreviewAction("download");
      setBusyBusinessId(qrPreviewData.businessId);

      if (Platform.OS === "web") {
        await downloadQrCodeOnWeb(qrPreviewData, {
          allowShareFallback: false,
        });
      } else {
        await saveQrCodeToDevice(qrPreviewData);
      }
    } catch (error: any) {
      Alert.alert(
        "QR download failed",
        error?.message || "Could not download the QR code."
      );
    } finally {
      setQrRenderData(null);
      setBusyBusinessId(null);
      setQrPreviewAction(null);
    }
  };

  const shareQrCodeFromPreview = async () => {
    if (!qrPreviewData) return;

    try {
      setQrPreviewAction("share");
      setBusyBusinessId(qrPreviewData.businessId);

      if (Platform.OS === "web") {
        await shareQrCodeOnWeb(qrPreviewData);
      } else {
        await shareQrCodeFromDevice(qrPreviewData);
      }
    } catch (error: any) {
      Alert.alert(
        "QR share failed",
        error?.message || "Could not share the QR code."
      );
    } finally {
      setQrRenderData(null);
      setBusyBusinessId(null);
      setQrPreviewAction(null);
    }
  };

  const copyQrPreviewLink = async () => {
    if (!qrPreviewData) return;

    try {
      setQrPreviewAction("copy");
      await Clipboard.setStringAsync(qrPreviewData.cardLink);
      Alert.alert("Copied", "The public card link has been copied.");
    } catch (error: any) {
      Alert.alert("Copy failed", error?.message || "Could not copy the link.");
    } finally {
      setQrPreviewAction(null);
    }
  };

  const clearLocalOwnerState = () => {
    setOwner(null);
    setBusinesses([]);
    setSubscription(null);
    setLoadingBusinesses(false);
    setLoadingSubscription(false);
  };

  const performSignOut = async () => {
    if (signingOut) return;

    try {
      setSigningOut(true);
      await googleSignOut();
      clearLocalOwnerState();
      router.replace("/directory" as any);
    } catch (error: any) {
      Alert.alert(
        "Sign out failed",
        error?.message || "Could not sign out. Please try again."
      );
    } finally {
      setSigningOut(false);
    }
  };

  const handleSignOut = () => {
    if (signingOut) return;

    if (Platform.OS === "web") {
      const confirmed =
        typeof window === "undefined" ||
        typeof window.confirm !== "function" ||
        window.confirm("Sign out of Soko Grid owner dashboard?");

      if (confirmed) {
        void performSignOut();
      }

      return;
    }

    Alert.alert(
      "Sign out?",
      "You will need to sign in again to manage your card.",
      [
        { text: "Cancel", style: "cancel" },
        {
          text: "Sign Out",
          style: "destructive",
          onPress: () => {
            void performSignOut();
          },
        },
      ]
    );
  };

  const handleDeleteDraft = async (business: BizRow) => {
    if (!ownerId) return;

    const status = normalizeStatus(business.status);

    if (status !== "draft") {
      Alert.alert(
        "Cannot delete this business",
        "Only draft businesses can be deleted by the owner."
      );
      return;
    }

    Alert.alert(
      "Delete draft?",
      `Delete "${
        business.name || "this business draft"
      }"?\n\nThis will hide the draft from your dashboard.`,
      [
        { text: "Cancel", style: "cancel" },
        {
          text: "Delete Draft",
          style: "destructive",
          onPress: async () => {
            try {
              setBusyBusinessId(business.id);
              await deleteDraftBusiness(business.id, ownerId);
              await persistOwnerOnboardingStep("basics");
              Alert.alert("Deleted", "The draft business has been removed.");
            } catch (error: any) {
              Alert.alert(
                "Delete failed",
                error?.message || "Could not delete this draft."
              );
            } finally {
              setBusyBusinessId(null);
            }
          },
        },
      ]
    );
  };

  const handleWithdrawPending = async (business: BizRow) => {
    if (!ownerId) return;

    const status = normalizeStatus(business.status);

    if (status !== "pending") {
      showDashboardMessage(
        "Cannot withdraw",
        "Only pending businesses can be withdrawn."
      );
      return;
    }

    const title = "Withdraw submission?";
    const message = `Withdraw "${
      business.name || "this business"
    }" from admin review?\n\nIt will return to draft so you can edit or delete it.`;

    const performWithdraw = async () => {
      try {
        setBusyBusinessId(business.id);
        await withdrawPendingBusiness(business.id, ownerId);

        showDashboardMessage(
          "Withdrawn",
          "The business is now back in draft. You can edit and resubmit it."
        );
      } catch (error: any) {
        showDashboardMessage(
          "Withdraw failed",
          error?.message || "Could not withdraw this business."
        );
      } finally {
        setBusyBusinessId(null);
      }
    };

    if (Platform.OS === "web") {
      const confirmed =
        typeof window === "undefined" ||
        typeof window.confirm !== "function" ||
        window.confirm(`${title}\n\n${message}`);

      if (confirmed) {
        void performWithdraw();
      }

      return;
    }

    Alert.alert(
      title,
      message,
      [
        { text: "Cancel", style: "cancel" },
        {
          text: "Withdraw",
          style: "destructive",
          onPress: () => {
            void performWithdraw();
          },
        },
      ]
    );
  };

  const handleDeactivateApproved = async (business: BizRow) => {
    if (!ownerId) return;

    if (normalizeStatus(business.status) !== "approved") {
      showDashboardMessage(
        "Cannot deactivate",
        "Only approved businesses can be deactivated."
      );
      return;
    }

    try {
      setBusyBusinessId(business.id);
      await deactivateApprovedBusiness(business.id, ownerId);
      showDashboardMessage("Updated", "Your business has been deactivated.");
    } catch {
      showDashboardMessage(
        "Update failed",
        "Could not update business status. Please try again."
      );
    } finally {
      setBusyBusinessId(null);
    }
  };

  const handleReactivateApproved = async (business: BizRow) => {
    if (!ownerId) return;

    try {
      setBusyBusinessId(business.id);
      await reactivateApprovedBusiness(business.id, ownerId);
      showDashboardMessage("Updated", "Your business is active again.");
    } catch {
      showDashboardMessage(
        "Update failed",
        "Could not update business status. Please try again."
      );
    } finally {
      setBusyBusinessId(null);
    }
  };

  const handleDeleteApproved = (business: BizRow) => {
    if (!ownerId) return;

    if (business.isDeactivated !== true) {
      showDashboardMessage(
        "Deactivate first",
        "Please deactivate this business before deleting it."
      );
      return;
    }

    const title = "Delete business?";
    const message = `Delete "${
      business.name || "this business"
    }"?\n\nThis will hide it from your dashboard and public views.`;

    const performDelete = async () => {
      try {
        setBusyBusinessId(business.id);
        await deleteApprovedBusiness(business.id, ownerId);
        showDashboardMessage("Deleted", "Your business has been deleted.");
      } catch {
        showDashboardMessage(
          "Delete failed",
          "Could not update business status. Please try again."
        );
      } finally {
        setBusyBusinessId(null);
      }
    };

    if (Platform.OS === "web") {
      const confirmed =
        typeof window === "undefined" ||
        typeof window.confirm !== "function" ||
        window.confirm(`${title}\n\n${message}`);

      if (confirmed) {
        void performDelete();
      }

      return;
    }

    Alert.alert(title, message, [
      { text: "Cancel", style: "cancel" },
      {
        text: "Delete Business",
        style: "destructive",
        onPress: () => {
          void performDelete();
        },
      },
    ]);
  };

  if (ownerError) {
    return (
      <SafeAreaView style={{ flex: 1, backgroundColor: "#FFFFFF" }}>
        <View style={centerWrap}>
          <Text style={loadingText}>
            We could not prepare your owner account. Please sign in again or
            contact support.
          </Text>
        </View>
      </SafeAreaView>
    );
  }

  if (!authInitialized || (!!user && !ownerReady)) {
    return (
      <SafeAreaView style={{ flex: 1, backgroundColor: "#FFFFFF" }}>
        <View style={centerWrap}>
          <ActivityIndicator />

          <Text style={loadingText}>Loading dashboard...</Text>
        </View>
      </SafeAreaView>
    );
  }

  if (!user) {
    return null;
  }

  return (
    <SafeAreaView style={{ flex: 1, backgroundColor: "#FFFFFF" }}>
      <View style={{ flex: 1, backgroundColor: "#FFFFFF" }}>
        <View style={topBar}>
          <View style={{ flex: 1, minWidth: 0 }}>
            <Pressable onPress={goDirectory} style={topBackButton}>
              <Text style={topBackButtonText}>← Directory</Text>
            </Pressable>

            <Text style={screenTitle}>Owner Dashboard</Text>

            <Text style={screenSubtitle} numberOfLines={1}>
              Hello, {displayName}
            </Text>
          </View>

          {draftOrEditableBusiness ? (
            <Pressable onPress={goCreateOrContinue} style={topActionDark}>
              <Text style={topActionText}>Continue</Text>
            </Pressable>
          ) : !hasExistingBusiness ? (
            <Pressable onPress={goCreateOrContinue} style={topActionDark}>
              <Text style={topActionText}>+ New</Text>
            </Pressable>
          ) : null}
        </View>

        <ScrollView contentContainerStyle={{ padding: 16, paddingBottom: 40 }}>
          {loading ? (
            <View style={businessLoadingWrap}>
              <ActivityIndicator />

              <Text style={loadingText}>Loading businesses...</Text>
            </View>
          ) : businesses.length === 0 ? (
            <View style={emptyCard}>
              <Text style={emptyTitle}>No business card yet</Text>

              <Text style={emptyText}>
                Create your Soko Grid Smart Business Card to get discovered.
              </Text>

              <Pressable onPress={goCreateOrContinue} style={primaryWideBtn}>
                <Text style={primaryWideBtnText}>Start Onboarding</Text>
              </Pressable>

              <Pressable
                onPress={handleSignOut}
                disabled={signingOut}
                style={[signOutWideBtn, signingOut ? faded : null]}
              >
                <Text style={signOutWideBtnText}>
                  {signingOut ? "Signing out..." : "Sign Out"}
                </Text>
              </Pressable>
            </View>
          ) : (
            <View style={{ gap: 14 }}>
              {businesses.map((business) => {
                const status = normalizeStatus(business.status);
                const statusColors = getStatusColors(business);
                const lifecycle = resolveOwnerDashboardLifecycle(business);

                const isPending = status === "pending";
                const isDeactivated = business.isDeactivated === true;
                const canOwnerManageVisibility =
                  lifecycle.canManageApproved;
                const isLive = isLiveApprovedBusiness(business);
                const editable =
                  lifecycle.canEdit && authoritativeBusiness?.id === business.id;

                const busy = busyBusinessId === business.id;

                const cardLink = lifecycle.canViewPublic && business.slug
                  ? buildBusinessPublicCardLink(business.slug)
                  : "Not public yet";

                const attentionItems = buildAttentionItems(business);
                const expiryValue = getExpiryValue(business, subscription);

                return (
                  <View key={business.id} style={businessCard}>
                    <View style={businessHeaderRow}>
                      <View style={{ flex: 1, minWidth: 0 }}>
                        <Text style={businessName} numberOfLines={1}>
                          {business.name?.trim() || "Untitled business"}
                        </Text>

                        <Text style={businessSlug} numberOfLines={1}>
                          {lifecycle.canViewPublic && business.slug
                            ? `/p/${business.slug}`
                            : "Not public yet"}
                        </Text>
                      </View>

                      <View
                        style={[
                          statusPill,
                          {
                            borderColor: statusColors.border,
                            backgroundColor: statusColors.bg,
                          },
                        ]}
                      >
                        <Text
                          style={[
                            statusPillText,
                            {
                              color: statusColors.text,
                            },
                          ]}
                        >
                          {getStatusLabel(business)}
                        </Text>
                      </View>
                    </View>

                    <View style={detailsBox}>
                      {shouldShowAccountNumber(status) ? (
                        <DetailRow
                          label="Account No."
                          value={getAccountNumberLabel(business)}
                        />
                      ) : null}

                      <DetailRow
                        label="Subscription type"
                        value={getSubscriptionPlanLabel(
                          business,
                          subscription
                        )}
                      />

                      <DetailRow
                        label="Subscription status"
                        value={getSubscriptionStatusLabel(
                          business,
                          subscription,
                          loadingSubscription
                        )}
                      />

                      <DetailRow
                        label="Created"
                        value={formatDate(business.createdAt)}
                      />

                      <DetailRow
                        label="Expires"
                        value={formatDate(expiryValue)}
                      />

                      <DetailRow label="Card link" value={cardLink} />
                    </View>

                    {status === "changes_requested" &&
                    business.changesRequestMessage ? (
                      <MessageBox
                        tone="info"
                        title="Admin requested changes"
                        message={business.changesRequestMessage}
                      />
                    ) : null}

                    {status === "rejected" && business.rejectedReason ? (
                      <MessageBox
                        tone="danger"
                        title="Rejection reason"
                        message={business.rejectedReason}
                      />
                    ) : null}

                    {isPending ? (
                      <MessageBox
                        tone="warning"
                        title="Submitted"
                        message="Your business is waiting for admin review. You can withdraw it if you need to edit before approval."
                      />
                    ) : null}

                    {lifecycle.isAwaitingPublication ? (
                      <MessageBox
                        tone="info"
                        title="Approved - awaiting publication"
                        message="Your business is approved. Public profile and management actions will become available after publication."
                      />
                    ) : null}

                    {isDeactivated ? (
                      <MessageBox
                        tone="neutral"
                        title="Business is deactivated"
                        message="Your business is currently deactivated and hidden from public search and profile views."
                      />
                    ) : null}

                    {isLive && attentionItems.length > 0 ? (
                      <View style={attentionBox}>
                        <Text style={attentionTitle}>Need attention</Text>

                        {attentionItems.slice(0, 3).map((item) => (
                          <Text key={item} style={attentionText}>
                            • {item}
                          </Text>
                        ))}
                      </View>
                    ) : null}

                    <View style={actionWrap}>
                      {editable ? (
                        <Pressable
                          onPress={() => void goEdit(business, "onboarding")}
                          disabled={busy}
                          style={[actionDark, busy ? faded : null]}
                        >
                          <Text style={actionDarkText}>
                            {getPrimaryActionLabel(status)}
                          </Text>
                        </Pressable>
                      ) : null}

                      {lifecycle.canWithdraw ? (
                        <Pressable
                          onPress={() => handleWithdrawPending(business)}
                          disabled={busy}
                          style={[actionLightWarning, busy ? faded : null]}
                        >
                          <Text style={actionWarningText}>
                            {busy ? "Working..." : "Withdraw Submission"}
                          </Text>
                        </Pressable>
                      ) : null}

                      {lifecycle.canManageApproved && !isDeactivated ? (
                        <>
                          <Pressable
                            onPress={() => goView(business.slug)}
                            disabled={!business.slug || busy}
                            style={[actionDark, busy ? faded : null]}
                          >
                            <Text style={actionDarkText}>View Public Card</Text>
                          </Pressable>

                          <Pressable
                            onPress={() => copyCardLink(business)}
                            disabled={!business.slug || busy}
                            style={[actionLight, busy ? faded : null]}
                          >
                            <Text style={actionLightText}>Copy Link</Text>
                          </Pressable>

                          <Pressable
                            onPress={() => handleQrButtonPress(business)}
                            disabled={!business.slug || busy}
                            style={[actionLight, busy ? faded : null]}
                          >
                            <Text style={actionLightText}>
                              {busy ? "Working..." : "Save / Share QR Code"}
                            </Text>
                          </Pressable>

                          <Pressable
                            onPress={() => void goEdit(business, "approved")}
                            disabled={busy}
                            style={[actionLight, busy ? faded : null]}
                          >
                            <Text style={actionLightText}>Edit Business</Text>
                          </Pressable>

                          <Pressable
                            onPress={() =>
                              goManageItems(business.id, "approved")
                            }
                            disabled={busy}
                            style={[actionLight, busy ? faded : null]}
                          >
                            <Text style={actionLightText}>Manage Items</Text>
                          </Pressable>

                          <Pressable
                            onPress={() => shareCardLink(business)}
                            disabled={!business.slug || busy}
                            style={[actionLight, busy ? faded : null]}
                          >
                            <Text style={actionLightText}>Share</Text>
                          </Pressable>

                          {canOwnerManageVisibility ? (
                            <Pressable
                              onPress={() => {
                                void handleDeactivateApproved(business);
                              }}
                              disabled={busy}
                              style={[actionLightWarning, busy ? faded : null]}
                            >
                              <Text style={actionWarningText}>
                                {busy ? "Working..." : "Deactivate Business"}
                              </Text>
                            </Pressable>
                          ) : null}
                        </>
                      ) : null}

                      {canOwnerManageVisibility && isDeactivated ? (
                        <>
                          <Pressable
                            onPress={() => {
                              void handleReactivateApproved(business);
                            }}
                            disabled={busy}
                            style={[actionLightWarning, busy ? faded : null]}
                          >
                            <Text style={actionWarningText}>
                              {busy ? "Working..." : "Reactivate Business"}
                            </Text>
                          </Pressable>

                          <Pressable
                            onPress={() => handleDeleteApproved(business)}
                            disabled={busy}
                            style={[actionDanger, busy ? faded : null]}
                          >
                            <Text style={actionDangerText}>
                              {busy ? "Working..." : "Delete Business"}
                            </Text>
                          </Pressable>
                        </>
                      ) : null}

                      {status === "draft" ? (
                        <>
                          <Pressable
                            onPress={() =>
                              goManageItems(business.id, "onboarding")
                            }
                            disabled={busy}
                            style={[actionLight, busy ? faded : null]}
                          >
                            <Text style={actionLightText}>Manage Items</Text>
                          </Pressable>

                          <Pressable
                            onPress={() => handleDeleteDraft(business)}
                            disabled={busy}
                            style={[actionDanger, busy ? faded : null]}
                          >
                            <Text style={actionDangerText}>
                              {busy ? "Working..." : "Delete Draft"}
                            </Text>
                          </Pressable>
                        </>
                      ) : null}
                    </View>
                  </View>
                );
              })}

              <Pressable
                onPress={handleSignOut}
                disabled={signingOut}
                style={[signOutWideBtn, signingOut ? faded : null]}
              >
                <Text style={signOutWideBtnText}>
                  {signingOut ? "Signing out..." : "Sign Out"}
                </Text>
              </Pressable>
            </View>
          )}
        </ScrollView>

        <Modal
          visible={qrPreviewData !== null}
          transparent
          animationType="slide"
          onRequestClose={closeQrPreview}
        >
          <View style={qrPreviewBackdrop}>
            {qrPreviewData ? (
              <View style={qrPreviewPanel}>
                <View style={qrPreviewHeader}>
                  <Text style={qrPreviewTitle}>QR Code</Text>

                  <Pressable
                    onPress={closeQrPreview}
                    disabled={qrPreviewBusy}
                    style={[
                      qrPreviewHeaderClose,
                      qrPreviewBusy ? faded : null,
                    ]}
                  >
                    <Text style={qrPreviewHeaderCloseText}>Close</Text>
                  </Pressable>
                </View>

                <View style={qrPreviewImageWrap}>
                  <QRCode
                    value={qrPreviewData.cardLink}
                    size={qrPreviewSize}
                    color="#111827"
                    backgroundColor="#FFFFFF"
                  />

                  <Text
                    style={qrPreviewCaptionText}
                    numberOfLines={2}
                    ellipsizeMode="tail"
                  >
                    {qrPreviewCaption}
                  </Text>
                </View>

                <View style={qrPreviewActions}>
                  <Pressable
                    onPress={downloadQrCodeFromPreview}
                    disabled={qrPreviewBusy}
                    style={[
                      qrPreviewPrimaryAction,
                      qrPreviewBusy ? faded : null,
                    ]}
                  >
                    <Text style={qrPreviewPrimaryText}>
                      {qrPreviewAction === "download"
                        ? "Working..."
                        : "Download QR Code"}
                    </Text>
                  </Pressable>

                  <Pressable
                    onPress={shareQrCodeFromPreview}
                    disabled={qrPreviewBusy}
                    style={[
                      qrPreviewSecondaryAction,
                      qrPreviewBusy ? faded : null,
                    ]}
                  >
                    <Text style={qrPreviewSecondaryText}>
                      {qrPreviewAction === "share"
                        ? "Working..."
                        : "Share QR Code"}
                    </Text>
                  </Pressable>

                  <Pressable
                    onPress={copyQrPreviewLink}
                    disabled={qrPreviewBusy}
                    style={[
                      qrPreviewSecondaryAction,
                      qrPreviewBusy ? faded : null,
                    ]}
                  >
                    <Text style={qrPreviewSecondaryText}>
                      {qrPreviewAction === "copy"
                        ? "Working..."
                        : "Copy Business Link"}
                    </Text>
                  </Pressable>

                  <Pressable
                    onPress={closeQrPreview}
                    disabled={qrPreviewBusy}
                    style={[
                      qrPreviewCloseAction,
                      qrPreviewBusy ? faded : null,
                    ]}
                  >
                    <Text style={qrPreviewCloseText}>Close</Text>
                  </Pressable>
                </View>
              </View>
            ) : null}
          </View>
        </Modal>

        <View
          ref={qrCaptureRef}
          collapsable={false}
          pointerEvents="none"
          style={qrCaptureWrap}
        >
          <View style={qrDownloadCard}>
            {qrRenderData ? (
              <>
                <View style={qrDownloadQrWrap}>
                  <QRCode
                    value={qrRenderData.cardLink}
                    size={QR_RENDER_QR_SIZE}
                    color="#111827"
                    backgroundColor="#FFFFFF"
                    getRef={(ref) => {
                      qrSvgRef.current = ref as QrSvgRef | null;
                    }}
                  />
                </View>

                <Text
                  style={[
                    qrDownloadHelperText,
                    {
                      fontSize: scaleQrCardValue(qrCaptionFontSize),
                      lineHeight: scaleQrCardValue(qrCaptionFontSize + 12),
                    },
                  ]}
                  numberOfLines={2}
                  ellipsizeMode="tail"
                >
                  {qrCaption}
                </Text>
              </>
            ) : null}
          </View>
        </View>
      </View>
    </SafeAreaView>
  );
}

function DetailRow({ label, value }: { label: string; value: string }) {
  return (
    <View style={detailRow}>
      <Text style={detailLabel}>{label}</Text>
      <Text style={detailValue} numberOfLines={3}>
        {value || "Not set"}
      </Text>
    </View>
  );
}

function MessageBox({
  tone,
  title,
  message,
}: {
  tone: "info" | "danger" | "warning" | "neutral";
  title: string;
  message: string;
}) {
  const colors =
    tone === "info"
      ? {
          border: "#BFDBFE",
          bg: "#EFF6FF",
          text: "#1E40AF",
        }
      : tone === "danger"
      ? {
          border: "#FCA5A5",
          bg: "#FEF2F2",
          text: "#991B1B",
        }
      : tone === "warning"
      ? {
          border: "#FCD34D",
          bg: "#FFFBEB",
          text: "#92400E",
        }
      : {
          border: "#E5E7EB",
          bg: "#F9FAFB",
          text: "#374151",
        };

  return (
    <View
      style={{
        marginTop: 12,
        borderWidth: 1,
        borderColor: colors.border,
        backgroundColor: colors.bg,
        borderRadius: 14,
        padding: 10,
      }}
    >
      <Text style={{ color: colors.text, fontWeight: "900" }}>{title}</Text>

      <Text
        style={{
          marginTop: 4,
          color: colors.text,
          fontWeight: "700",
          lineHeight: 20,
        }}
      >
        {message}
      </Text>
    </View>
  );
}

const centerWrap = {
  flex: 1,
  alignItems: "center" as const,
  justifyContent: "center" as const,
  padding: 20,
};

const loadingText = {
  marginTop: 12,
  color: "#6B7280",
  fontWeight: "800" as const,
};

const topBar = {
  paddingHorizontal: 16,
  paddingVertical: 14,
  borderBottomWidth: 1,
  borderBottomColor: "#F1F5F9",
  flexDirection: "row" as const,
  alignItems: "center" as const,
  justifyContent: "space-between" as const,
  gap: 10,
};

const topBackButton = {
  alignSelf: "flex-start" as const,
  marginBottom: 8,
  paddingVertical: 6,
  paddingHorizontal: 10,
  borderRadius: 999,
  backgroundColor: "#F3F4F6",
};

const topBackButtonText = {
  color: "#111827",
  fontSize: 13,
  fontWeight: "900" as const,
};

const screenTitle = {
  fontSize: 22,
  fontWeight: "900" as const,
  color: "#111827",
};

const screenSubtitle = {
  marginTop: 2,
  color: "#6B7280",
  fontWeight: "800" as const,
};

const topActionDark = {
  minHeight: 42,
  paddingHorizontal: 16,
  borderRadius: 14,
  backgroundColor: "#111827",
  alignItems: "center" as const,
  justifyContent: "center" as const,
};

const topActionText = {
  color: "#FFFFFF",
  fontWeight: "900" as const,
};

const businessLoadingWrap = {
  paddingVertical: 30,
  alignItems: "center" as const,
  justifyContent: "center" as const,
};

const emptyCard = {
  borderWidth: 1,
  borderColor: "#E5E7EB",
  borderRadius: 18,
  padding: 16,
  backgroundColor: "#FFFFFF",
};

const emptyTitle = {
  fontSize: 18,
  fontWeight: "900" as const,
  color: "#111827",
};

const emptyText = {
  marginTop: 8,
  color: "#6B7280",
  fontWeight: "700" as const,
  lineHeight: 21,
};

const primaryWideBtn = {
  marginTop: 16,
  minHeight: 52,
  borderRadius: 16,
  backgroundColor: "#111827",
  alignItems: "center" as const,
  justifyContent: "center" as const,
};

const primaryWideBtnText = {
  color: "#FFFFFF",
  fontWeight: "900" as const,
  fontSize: 16,
};

const businessCard = {
  borderWidth: 1,
  borderColor: "#E5E7EB",
  borderRadius: 18,
  padding: 14,
  backgroundColor: "#FFFFFF",
};

const businessHeaderRow = {
  flexDirection: "row" as const,
  alignItems: "flex-start" as const,
  gap: 10,
};

const businessName = {
  fontSize: 19,
  fontWeight: "900" as const,
  color: "#111827",
};

const businessSlug = {
  marginTop: 4,
  fontSize: 14,
  color: "#6B7280",
  fontWeight: "800" as const,
};

const statusPill = {
  borderWidth: 1,
  borderRadius: 999,
  paddingHorizontal: 12,
  minHeight: 34,
  alignItems: "center" as const,
  justifyContent: "center" as const,
};

const statusPillText = {
  fontWeight: "900" as const,
  fontSize: 12,
};

const detailsBox = {
  marginTop: 14,
  borderWidth: 1,
  borderColor: "#F1F5F9",
  borderRadius: 16,
  padding: 12,
  backgroundColor: "#F9FAFB",
  gap: 10,
};

const detailRow = {
  flexDirection: "row" as const,
  alignItems: "flex-start" as const,
  justifyContent: "space-between" as const,
  gap: 14,
};

const detailLabel = {
  flex: 1,
  color: "#6B7280",
  fontWeight: "900" as const,
};

const detailValue = {
  flex: 1.25,
  color: "#111827",
  fontWeight: "900" as const,
  textAlign: "right" as const,
  lineHeight: 19,
};

const attentionBox = {
  marginTop: 12,
  borderWidth: 1,
  borderColor: "#FED7AA",
  backgroundColor: "#FFF7ED",
  borderRadius: 14,
  padding: 10,
};

const attentionTitle = {
  color: "#9A3412",
  fontWeight: "900" as const,
};

const attentionText = {
  marginTop: 5,
  color: "#9A3412",
  fontWeight: "700" as const,
  lineHeight: 19,
};

const actionWrap = {
  marginTop: 14,
  flexDirection: "row" as const,
  flexWrap: "wrap" as const,
  gap: 10,
};

const baseAction = {
  minHeight: 48,
  borderRadius: 14,
  paddingHorizontal: 14,
  alignItems: "center" as const,
  justifyContent: "center" as const,
  flexGrow: 1,
  flexBasis: "45%" as const,
};

const actionDark = {
  ...baseAction,
  backgroundColor: "#111827",
};

const actionDarkText = {
  color: "#FFFFFF",
  fontWeight: "900" as const,
  fontSize: 15,
  textAlign: "center" as const,
};

const actionLight = {
  ...baseAction,
  backgroundColor: "#FFFFFF",
  borderWidth: 1,
  borderColor: "#E5E7EB",
};

const actionLightText = {
  color: "#111827",
  fontWeight: "900" as const,
  fontSize: 15,
  textAlign: "center" as const,
};

const actionLightWarning = {
  ...baseAction,
  backgroundColor: "#FFFBEB",
  borderWidth: 1,
  borderColor: "#FCD34D",
};

const actionWarningText = {
  color: "#92400E",
  fontWeight: "900" as const,
  fontSize: 15,
  textAlign: "center" as const,
};

const actionDanger = {
  ...baseAction,
  backgroundColor: "#FEF2F2",
  borderWidth: 1,
  borderColor: "#FCA5A5",
};

const actionDangerText = {
  color: "#991B1B",
  fontWeight: "900" as const,
  fontSize: 15,
  textAlign: "center" as const,
};

const signOutWideBtn = {
  minHeight: 52,
  borderRadius: 16,
  borderWidth: 1,
  borderColor: "#FCA5A5",
  backgroundColor: "#FEF2F2",
  alignItems: "center" as const,
  justifyContent: "center" as const,
};

const signOutWideBtnText = {
  color: "#991B1B",
  fontWeight: "900" as const,
  fontSize: 16,
};

const qrPreviewBackdrop = {
  flex: 1,
  backgroundColor: "rgba(17, 24, 39, 0.48)",
  justifyContent: "flex-end" as const,
  padding: 16,
};

const qrPreviewPanel = {
  width: "100%" as const,
  maxWidth: 430,
  alignSelf: "center" as const,
  borderRadius: 18,
  backgroundColor: "#FFFFFF",
  padding: 16,
  gap: 14,
};

const qrPreviewHeader = {
  flexDirection: "row" as const,
  alignItems: "center" as const,
  justifyContent: "space-between" as const,
  gap: 12,
};

const qrPreviewTitle = {
  color: "#111827",
  fontSize: 20,
  fontWeight: "900" as const,
};

const qrPreviewHeaderClose = {
  minHeight: 38,
  borderRadius: 12,
  paddingHorizontal: 12,
  alignItems: "center" as const,
  justifyContent: "center" as const,
  backgroundColor: "#F3F4F6",
};

const qrPreviewHeaderCloseText = {
  color: "#111827",
  fontSize: 13,
  fontWeight: "900" as const,
};

const qrPreviewImageWrap = {
  alignItems: "center" as const,
  justifyContent: "center" as const,
  paddingVertical: 8,
  gap: 12,
};

const qrPreviewCaptionText = {
  color: "#111827",
  fontSize: 18,
  lineHeight: 24,
  fontWeight: "900" as const,
  textAlign: "center" as const,
};

const qrPreviewActions = {
  gap: 10,
};

const qrPreviewPrimaryAction = {
  minHeight: 48,
  borderRadius: 14,
  backgroundColor: "#111827",
  alignItems: "center" as const,
  justifyContent: "center" as const,
  paddingHorizontal: 14,
};

const qrPreviewPrimaryText = {
  color: "#FFFFFF",
  fontSize: 15,
  fontWeight: "900" as const,
  textAlign: "center" as const,
};

const qrPreviewSecondaryAction = {
  minHeight: 48,
  borderRadius: 14,
  borderWidth: 1,
  borderColor: "#E5E7EB",
  backgroundColor: "#FFFFFF",
  alignItems: "center" as const,
  justifyContent: "center" as const,
  paddingHorizontal: 14,
};

const qrPreviewSecondaryText = {
  color: "#111827",
  fontSize: 15,
  fontWeight: "900" as const,
  textAlign: "center" as const,
};

const qrPreviewCloseAction = {
  minHeight: 48,
  borderRadius: 14,
  backgroundColor: "#F3F4F6",
  alignItems: "center" as const,
  justifyContent: "center" as const,
  paddingHorizontal: 14,
};

const qrPreviewCloseText = {
  color: "#374151",
  fontSize: 15,
  fontWeight: "900" as const,
  textAlign: "center" as const,
};

const qrCaptureWrap = {
  position: "absolute" as const,
  left: -10000,
  top: -10000,
  width: QR_RENDER_CARD_WIDTH,
  height: QR_RENDER_CARD_HEIGHT,
  backgroundColor: "#FFFFFF",
};

const qrDownloadCard = {
  width: QR_RENDER_CARD_WIDTH,
  height: QR_RENDER_CARD_HEIGHT,
  backgroundColor: "#FFFFFF",
  position: "relative" as const,
  alignItems: "center" as const,
};

const qrDownloadQrWrap = {
  position: "absolute" as const,
  top: QR_RENDER_QR_TOP,
  left: (QR_RENDER_CARD_WIDTH - QR_RENDER_QR_SIZE) / 2,
  width: QR_RENDER_QR_SIZE,
  height: QR_RENDER_QR_SIZE,
};

const qrDownloadHelperText = {
  position: "absolute" as const,
  top: QR_RENDER_LABEL_TOP,
  left: (QR_RENDER_CARD_WIDTH - QR_RENDER_LABEL_MAX_WIDTH) / 2,
  width: QR_RENDER_LABEL_MAX_WIDTH,
  color: "#111827",
  fontSize: scaleQrCardValue(56),
  lineHeight: scaleQrCardValue(68),
  fontWeight: "900" as const,
  textAlign: "center" as const,
};

const faded = {
  opacity: 0.55,
};
